// frontend/src/types/practiceArea.ts
// Mirrors backend/app/constants/practice_areas.py — keep in sync manually.
// Whenever the backend enum changes, update this file + ship a data migration
// if existing matter rows need retagging.

export const PRACTICE_AREAS = [
  'criminal',
  'civil_property',
  'civil_consumer',
  'family',
  'corporate',
  'tax',
  'labor',
  'other',
] as const;

export type PracticeArea = (typeof PRACTICE_AREAS)[number];

// The CPC-suit practice areas — mirrors SUIT_PRACTICE_AREAS in the backend
// constants. Family, tax and labor stay OUT until a toolkit honestly serves
// them (writs, suit-spine Tier 3).
export const SUIT_PRACTICE_AREAS: ReadonlySet<PracticeArea> = new Set<PracticeArea>([
  'civil_property', 'civil_consumer', 'other',
]);

// Writ practice areas (suit-spine Tier 3) — everyone in the civil role; the
// writ is the universal public-law remedy. Mirrors WRIT_PRACTICE_AREAS in the
// backend constants.
export const WRIT_PRACTICE_AREAS: ReadonlySet<PracticeArea> = new Set<PracticeArea>([
  'civil_property', 'civil_consumer', 'family', 'tax', 'labor', 'other',
]);

// Capability map: feature -> practice areas it currently produces correct
// output for. A feature ABSENT from the map is available everywhere.
// Mirrors FEATURE_PRACTICE_AREAS in the backend constants module.
export const FEATURE_PRACTICE_AREAS: Readonly<Record<string, ReadonlySet<PracticeArea>>> = {
  // case_theory: widened for CIVIL-SUIT MODE — the engine now carries a civil
  // stage-builder family; criminal behaviour is unchanged, suit areas route to
  // the civil builders. Mirrors the backend `frozenset({"criminal"}) |
  // SUIT_PRACTICE_AREAS` widening in app/constants/practice_areas.py.
  case_theory: new Set<PracticeArea>(['criminal', ...SUIT_PRACTICE_AREAS]),
  fir_analyzer: new Set<PracticeArea>(['criminal']),
  // Mirrors backend practice_areas.py — the hearing brief prompt is
  // criminal-side (bail / remand / charge framing). A civil prompt is a
  // separate, funded piece of work; until then civil must not be offered it.
  hearing_prep: new Set<PracticeArea>(['criminal']),
  bail_calculator: new Set<PracticeArea>(['criminal']),
  // /calendar now serves BOTH boards — App.tsx admits criminal and civil, and
  // the page picks its ranking from the active role (liberty clocks vs
  // time-bar). This entry previously hid the item from civil lawyers because
  // the route bounced them to '/'; that was the symptom, and the route is
  // fixed. Keep this in step with RoleRoute — a narrower set here hides a page
  // the API will happily serve.
  calendar: new Set<PracticeArea>([
    'criminal',
    'civil_property',
    'civil_consumer',
    'family',
    'tax',
    'labor',
    'other',
  ]),
  // civil-suit features (widened from {civil_consumer} — suit-spine Tier 0)
  notice_analyzer: SUIT_PRACTICE_AREAS,
  forum_calculator: SUIT_PRACTICE_AREAS,
  // s.34 set-aside grounds checklist (suit-spine Tier 2)
  s34_grounds: SUIT_PRACTICE_AREAS,
  // writ maintainability checklist (suit-spine Tier 3) — the whole civil role
  writ_maintainability: WRIT_PRACTICE_AREAS,
  // The Civil Practice Guide is documentation, not a billable tool: it is
  // gated here (so it only appears on the civil board) but deliberately has NO
  // entry in the backend FEATURE_PRACTICE_AREAS entitlement map — reading the
  // manual must never 402.
  civil_guide: new Set<PracticeArea>(['civil_consumer']),
  // Start Here is a page of entry points (FIR, arrest, bail, drafts), not a
  // billable tool: gated here so it only appears on the criminal board, and
  // like civil_guide it has NO backend entitlement entry — opening it must
  // never 402. Each card's own tool carries its own gate.
  start_here: new Set<PracticeArea>(['criminal']),
};

// Backward-compatible derived view (features restricted to exactly {criminal}).
export const CRIMINAL_ONLY_FEATURES: ReadonlySet<string> = new Set(
  Object.entries(FEATURE_PRACTICE_AREAS)
    .filter(([, areas]) => areas.size === 1 && areas.has('criminal'))
    .map(([f]) => f),
);

export function isValidPracticeArea(value: unknown): value is PracticeArea {
  return typeof value === 'string' && (PRACTICE_AREAS as readonly string[]).includes(value);
}

/**
 * True if the feature currently produces correct output for the practice area.
 * Mirrors app.constants.practice_areas.is_available_for in the backend, with
 * ONE intentional difference: a null/undefined practice_area falls through as
 * available (the "All areas" lens shows the union). The backend's 409 guard is
 * the authoritative block; this drives UI affordances only.
 */
export function isAvailableFor(
  feature: string,
  practiceArea: PracticeArea | null | undefined,
): boolean {
  const allowed = FEATURE_PRACTICE_AREAS[feature];
  if (!allowed) return true; // unmapped feature: available everywhere
  if (practiceArea == null) return true; // "All areas" lens: show the union
  return allowed.has(practiceArea);
}

/**
 * Resolve effective_practice_area for UI gating.
 * Precedence: matter.practice_area → user.primary_practice_area → null.
 * Mirrors the resolution logic in backend/app/api/v1/case_theory.py.
 *
 * Matter-scoped pages call this WITHOUT the lens, so a matter always shows its
 * own practice area's tools regardless of the global switcher (design §2 R2).
 */
export function effectivePracticeArea(
  matterPA: PracticeArea | null | undefined,
  userPA: PracticeArea | null | undefined,
): PracticeArea | null {
  return matterPA ?? userPA ?? null;
}

/**
 * Resolve the GLOBAL workspace lens for sidebar/nav gating.
 * Precedence: active lens (switcher) → user.primary_practice_area → null.
 * null means "All areas" — isAvailableFor then shows the union.
 */
export function effectiveLens(
  activeLens: PracticeArea | null | undefined,
  userPA: PracticeArea | null | undefined,
): PracticeArea | null {
  return activeLens ?? userPA ?? null;
}

// --- Roles: grouping over PRACTICE_AREAS. Mirrors backend ROLES. ------------
export type Role = 'criminal' | 'civil' | 'corporate';

export const ROLES: Readonly<Record<Role, ReadonlySet<PracticeArea>>> = {
  criminal: new Set<PracticeArea>(['criminal']),
  civil: new Set<PracticeArea>([
    'civil_property', 'civil_consumer', 'family', 'tax', 'labor', 'other',
  ]),
  corporate: new Set<PracticeArea>(['corporate']),
};

// Human labels for a workspace role — the ONE name each workspace goes by
// (login chooser, header switcher, dashboard greeting). Never label a
// workspace by its representative practice area: "Consumer Disputes" for a
// property lawyer's civil workspace is wrong and confusing.
//
// Each name carries "Practice" because these labels are read as an apposition
// to the advocate: the dashboard greeting renders "Good evening, alok —
// {label}", and the bare area name there described the lawyer rather than the
// workspace. Nowhere may retype these strings — PracticeAreaChooser held its
// own hardcoded copies and had already drifted ("Criminal Practice" on the
// login card vs "Criminal" in the greeting for the same workspace).
export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  criminal: 'Criminal Practice',
  civil: 'Consumer & Civil Practice',
  corporate: 'Corporate Practice',
};

/**
 * The name to show for the workspace a lawyer is CURRENTLY in.
 *
 * `activeRole` (sessionStorage, per-tab) is the boundary every API call is
 * scoped by, so it wins; `roleOf(lens)` only covers sessions predating roles.
 * Never derive this from `primary_practice_area` — that is a stored preference
 * a workspace switch never touches, which is exactly how the sidebar chip came
 * to read "Criminal Practice" in a civil workspace while the switcher directly
 * above it read "Consumer & Civil".
 *
 * Returns null when no workspace can be established ("All areas"), so callers
 * keep their own fallback (the sidebar shows the email).
 */
export function workspaceLabel(
  activeRole: Role | null | undefined,
  lens: PracticeArea | null | undefined,
): string | null {
  const role = activeRole ?? roleOf(lens);
  return role ? ROLE_LABELS[role] : null;
}

export function roleOf(area: PracticeArea | null | undefined): Role | null {
  if (!area) return null;
  for (const [role, areas] of Object.entries(ROLES) as [Role, ReadonlySet<PracticeArea>][]) {
    if (areas.has(area)) return role;
  }
  return null;
}

export function areasOf(role: Role): ReadonlySet<PracticeArea> {
  return ROLES[role] ?? new Set();
}

/**
 * The one practice area that represents a role when exactly one must be stored
 * (a client's tag, a workspace anchor). Mirrors `_ROLE_DEFAULT_AREA` in
 * backend/app/constants/practice_areas.py so frontend- and backend-stamped
 * areas can never diverge.
 */
export const ROLE_DEFAULT_AREA: Record<Role, PracticeArea> = {
  criminal: 'criminal',
  civil: 'civil_consumer',
  corporate: 'corporate',
};

export function rolesOfUser(practiceAreas: readonly PracticeArea[] | undefined): Set<Role> {
  const out = new Set<Role>();
  for (const a of practiceAreas ?? []) {
    const r = roleOf(a);
    if (r) out.add(r);
  }
  return out;
}

/**
 * The roles whose CONTENT a user may be shown (template library, act filter).
 * Criminal is the only vertical on sale, so a profile that holds no role at all
 * (not loaded yet, or the corrupt-data case) is offered criminal and nothing
 * else — an allow-list, never "everything". Civil and corporate appear only
 * when the backend has put them on the user's `practice_areas`.
 */
export function offeredRoles(practiceAreas: readonly PracticeArea[] | undefined): Set<Role> {
  const held = rolesOfUser(practiceAreas);
  return held.size > 0 ? held : new Set<Role>(['criminal']);
}

